import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { videos, getRelatedVideos } from '@/mocks/videos';
import { categorySlugMap } from '@/mocks/categories';
import type { Video } from '@/mocks/videos';

export default function VideoPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const [video, setVideo] = useState<Video | null>(null);
  const [related, setRelated] = useState<Video[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [videoId]);

  useEffect(() => {
    if (videoId) {
      const found = videos.find((v) => v.id === videoId);
      if (found) {
        setVideo(found);
        setRelated(getRelatedVideos(videoId, found.category));
      }
    }
  }, [videoId]);

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm mb-4">视频不存在或已下架</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = categorySlugMap[video.category] || video.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.updateDate,
    image: video.thumbnail,
    author: {
      '@type': 'Organization',
      name: '久久在线观看',
    },
    publisher: {
      '@type': 'Organization',
      name: '久久在线观看',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mobiang.cn/video/${video.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://mobiang.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://mobiang.cn/category/${video.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: video.title,
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${video.title} - 久久在线观看高清视频`,
    description: video.description,
    url: `https://mobiang.cn/video/${video.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName, path: `/category/${video.category}` },
                { label: video.title },
              ]}
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="relative aspect-video rounded-xl overflow-hidden bg-background-950">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 久久在线观看高清视频`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer group">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 group-hover:bg-primary-500 transition-colors">
                      <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
                    </div>
                  </div>
                  <span className="absolute bottom-3 right-3 bg-black/75 text-white text-sm px-3 py-1 rounded whitespace-nowrap">
                    {video.duration}
                  </span>
                </div>

                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-6">
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <strong className="text-foreground-800">{video.rating}</strong> 分
                  </span>
                  <span>{video.year}</span>
                  <span>{categoryName}</span>
                  <span>{video.views.toLocaleString()} 次观看</span>
                  <span>更新于 {video.updateDate}</span>
                </div>

                {video.director && (
                  <div className="mt-4 flex items-center gap-2 text-sm">
                    <span className="text-foreground-500">导演：</span>
                    <span className="text-foreground-700">{video.director}</span>
                  </div>
                )}

                {video.actors && video.actors.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-foreground-500">主演：</span>
                    <span className="text-foreground-700">{video.actors.join(' / ')}</span>
                  </div>
                )}

                {video.episodes && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-foreground-500">集数：</span>
                    <span className="text-foreground-700">共 {video.episodes} 集</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  {video.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-heading font-bold text-foreground-800 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                </div>
              </div>

              <div className="lg:col-span-1">
                <h3 className="text-base font-heading font-bold text-foreground-800 mb-4">相关推荐</h3>
                <div className="space-y-4">
                  {related.map((rel) => (
                    <Link key={rel.id} to={`/video/${rel.id}`} className="flex gap-3 group cursor-pointer">
                      <div className="w-32 aspect-video rounded-lg overflow-hidden shrink-0">
                        <img
                          src={rel.thumbnail}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex flex-col justify-center min-w-0">
                        <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1.5 flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                          {rel.rating}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}